import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ContactStateApiService } from '../services/contact-state-api.service';
import { ContactStateRequest, ContactStateResponse } from '../models/contact-state.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { NAME_PATTERN, normalizeForComparison } from '../../../shared/utils/text-normalization.utils';

@Component({
  selector: 'app-contact-state-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './contact-state-form.component.html'
})
export class ContactStateFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  stateId: number | null = null;

  // Form model
  name = '';
  private originalName = '';
  order = 1;

  // Lista de estados existentes (para validar duplicados)
  private existingStates: ContactStateResponse[] = [];

  constructor(
    private stateApi: ContactStateApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarEstadosExistentes();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.stateId = +id;
      this.cargarEstado(this.stateId);
    }
  }

  private cargarEstadosExistentes(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.stateApi.getStates(businessId).subscribe({
      next: (data) => { this.existingStates = data; },
      error: () => { /* Si falla, deja la validación al backend */ }
    });
  }

  private esNombreDuplicado(nombre: string): boolean {
    const target = normalizeForComparison(nombre);
    return this.existingStates.some(s =>
      normalizeForComparison(s.name) === target && s.id !== this.stateId
    );
  }

  private cargarEstado(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.stateApi.getStateById(id, businessId).subscribe({
      next: (estado) => {
        this.name = estado.name;
        this.originalName = estado.name;
        this.order = estado.order ?? 1;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el estado';
        this.loading = false;
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Estado de Contacto' : 'Nuevo Estado de Contacto';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    if (!NAME_PATTERN.test(this.name.trim())) {
      const msg = 'El nombre solo puede contener letras, espacios, apóstrofes y guiones';
      this.error = msg;
      this.toast.warning({ title: 'Nombre inválido', message: msg });
      return;
    }

    if (this.esNombreDuplicado(this.name)) {
      const msg = `Ya existe un estado con el nombre "${this.name.trim()}"`;
      this.error = msg;
      this.toast.warning({ title: 'Nombre duplicado', message: msg });
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: ContactStateRequest = {
      businessId,
      name: this.name.trim(),
      order: Number(this.order)
    };

    if (this.isEditing && this.stateId) {
      this.stateApi.updateState(this.stateId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Estado de contacto',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/estados']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el estado';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.stateApi.createState(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Estado de contacto', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/estados']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el estado';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-contactos/estados']);
  }
}
